import { Button, ButtonDropdown, Dialog, Spinner } from '@rio-cloud/rio-uikit';
import { useConsultants } from '../../../../hooks/useConsultants';
import ConsultantForm from '../form/ConsultantForm';
import { RestrictArea } from '../../../../components/restrictArea';
import { Consultant } from '../../../../models/consultant';

const ConsultantListContent = () => {
  const {
    consultants,
    loading,
    deleteLoading,
    error,
    showForm,
    editingConsultant,
    showDeleteDialog,
    consultantToDelete,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    editConsultant,
    addConsultant,
    userHasAccess,
    scrollContainerRef,
    isDragging,
    handleMouseDown,
    handleMouseLeave,
    handleMouseUp,
    handleMouseMove,
  } = useConsultants();

  if (loading || userHasAccess === null) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!userHasAccess) {
    return <RestrictArea showBackButton={false} />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600 text-center">
          <p className="text-lg font-semibold mb-2">Erro ao carregar consultores</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const consultantFeatures = (consultant: Consultant): string => {
    let status = ""
    if (consultant.groupConsultant) status = concatPipe(status, "GRUPO");
    if (consultant.specialist) status = concatPipe(status, "ESPECIALISTA");
    if (consultant.sendEmail) status = concatPipe(status, "EMAIL");
    return status
  }

  const concatPipe = (value: string, newValue: string): string => {
    if (value!) {
      value += " | "
    }
    return value += newValue
  }

  // Função auxiliar para impedir que o clique no texto inicie o drag da tabela
  const preventDragOnText = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="text-size-h2">Consultores</div>
        </div>
        <Button
          bsStyle={Button.PRIMARY}
          onClick={() => setShowForm(true)}
          iconName="rioglyph-plus-sign"
        >
          Novo Consultor
        </Button>
      </div>

      <div
        className={`bg-white rounded-lg shadow overflow-x-auto ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <table className={`w-full divide-y divide-gray-200 m-6 max-w-[940px] ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left font-medium text-gray-500 cursor-default">
                Nome
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 cursor-default">
                Email
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 cursor-default">
                Telefone
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 cursor-default">
                DN
              </th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 cursor-default"></th>
              <th className="px-6 py-3 text-right font-medium text-gray-500 sticky right-0 top-0 z-10 bg-gray-50 cursor-default"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {consultants.map((consultant) => (
              <tr key={consultant.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="font-medium text-gray-900 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.firstName} {consultant.lastName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.phoneNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultant.dn}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="text-gray-900 cursor-text inline-block"
                    onMouseDown={preventDragOnText}
                  >
                    {consultantFeatures(consultant)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right font-medium sticky right-0 top-0 z-10 bg-white cursor-default">
                  <div className="flex justify-end space-x-2">
                    <ButtonDropdown
                      title={<span className='rioglyph rioglyph-option-vertical' />}
                      items={[
                        {
                          value: 'Editar',
                          onSelect: () => handleEdit(consultant)
                        },
                        {
                          value: 'Excluir',
                          onSelect: () => handleDelete(consultant)
                        }
                      ]}
                      bsStyle='muted'
                      pullRight
                      noCaret
                      usePortal
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {consultants.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <p className="font-medium mb-2">Nenhum consultor encontrado</p>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <Dialog
          show={showForm}
          onClose={handleFormClose}
          showCloseButton={true}
          title={editingConsultant ? 'Editar Consultor' : 'Novo Consultor'}
          bsSize={Dialog.SIZE_SM}
          body={(
            <ConsultantForm
              consultant={editingConsultant}
              editConsultant={editConsultant}
              addConsultant={addConsultant}
              onSuccess={() => {
                handleFormSuccess();
              }}
              onCancel={() => {
                handleFormClose();
              }}
            />
          )}
        />
      )}

      <Dialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Confirmar Exclusão"
        bsSize={Dialog.SIZE_SM}
      >
        <div className="p-6">
          <p className="text-size-h4 text-gray-700 mb-4">
            Tem certeza que deseja excluir o consultor{' '}
            <strong>
              {consultantToDelete?.firstName} {consultantToDelete?.lastName}
            </strong>?
          </p>
          <p className="text-gray-600 mb-6">
            Esta ação não pode ser desfeita.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              bsStyle={Button.DANGER}
              onClick={confirmDelete}
              disabled={deleteLoading}
              iconName={deleteLoading ? "rioglyph-spinner" : undefined}
            >
              {deleteLoading ? "Excluindo..." : "Excluir"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

const ConsultantList = (props: any) => {
  return (
    <ConsultantListContent {...props} />
  );
};

export default ConsultantList;
